import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { Eye, EyeOff, Trash2, Flag } from "lucide-react";
import { ProfileAvatar } from "@/components/build/ProfileAvatar";
import { MemberLink } from "@/components/build/MemberLink";
import { ConfirmDialog } from "./ConfirmDialog";
import { CollaborationDetailModal } from "./CollaborationDetailModal";
import { timeAgo } from "./PostCard";

const AVATAR_TONES = ["bg-brand text-ink", "bg-ink text-brand", "bg-ink-soft text-paper"];

function AvatarChip({ label, photo, freelancerId, tone, first = false }) {
  const dim = "w-[26px] h-[26px]";
  return (
    <ProfileAvatar freelancerId={freelancerId} hasPhoto={Boolean(photo)} alt=""
      className={`${dim} rounded-full object-cover shrink-0 border-2 border-paper ${first ? "" : "-ml-[7px]"}`}
      fallback={
        <div className={`${dim} rounded-full flex items-center justify-center font-mont font-bold shrink-0 border-2 border-paper text-[9px] ${tone} ${first ? "" : "-ml-[7px]"}`}>
          {label}
        </div>
      } />
  );
}

/**
 * Collaboration Opportunities — Floor card, visual pass (2026-08) matching the approved
 * mockup ("NJM Build — Collaborations, standalone", frame 1a/1b): 4px accent spine, avatar
 * stack that reads as PEOPLE not data, role chips with a struck-through state once a role
 * fills, and a status-aware footer CTA. Same underlying data/actions as before (hide/report/
 * delete via the existing `/v1/community/posts/{id}` endpoints, View Opportunity opens the
 * unchanged `CollaborationDetailModal`) — this pass is visual only.
 */
export function CollaborationCard({ post, isModerator, viewerId, viewerIsFreelancer, onChanged, testIdSuffix = "", showRoleChips = true }) {
  const [showDetail, setShowDetail] = useState(false);
  const [autoOpenApplications, setAutoOpenApplications] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reported, setReported] = useState(false);
  const canDelete = isModerator || post.can_delete;
  // Dashboard Fixes (2026-11), Item 3 — same ownership/moderation check as `canDelete`
  // (creator-or-moderator); the capability already exists inside `CollaborationDetailModal`'s
  // "Manage this opportunity" panel, this just gives it a direct, visible entry point on the
  // card itself (Floor AND My Work, since both render this same component) instead of
  // requiring the creator to open the post and scroll to find it.
  const canManage = canDelete;
  const status = post.collab_status || "open";
  const openCount = post.collab_open_slots ?? (post.collab_role_slots || []).reduce((sum, s) => sum + s.quantity_required, 0);
  const complete = status === "open" && openCount === 0;
  const slots = post.collab_role_slots || [];
  const accepted = post.collab_accepted_members || [];

  const moderate = async (action, payload) => {
    setBusy(true);
    try {
      if (action === "delete") await api.delete(`/v1/community/posts/${post.id}`);
      else await api.post(`/v1/community/posts/${post.id}/${action}`, payload);
      onChanged?.();
    } catch { toast.error("Couldn't update this post"); }
    finally { setBusy(false); }
  };

  const report = async () => {
    try { await api.post(`/v1/community/posts/${post.id}/report`); setReported(true); toast.success("Reported to NJM"); }
    catch { toast.error("Couldn't report this post"); }
  };

  const stackAvatars = [
    { key: "creator", label: (post.author_name || "?")[0], photo: post.author_photo, freelancerId: post.author_freelancer_id, tone: AVATAR_TONES[0] },
    ...accepted.slice(0, 3).map((m, i) => ({ key: m.freelancer_id || i, label: (m.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2), photo: m.photo, freelancerId: m.freelancer_id, tone: AVATAR_TONES[(i + 1) % AVATAR_TONES.length] })),
  ];
  const dashedCount = status === "cancelled" ? 0 : Math.min(openCount, 3);

  const spineColor = status === "cancelled" ? "bg-red-300" : status === "closed" ? "bg-ink-soft/40" : complete ? "bg-ink" : "bg-brand";
  const cardBorder = post.hidden ? "border-red-300" : complete ? "border-black/10" : "border-black/10";

  return (
    <div data-testid={`community-post-${post.id}${testIdSuffix}`}
      className={`flex overflow-hidden rounded-xl border ${cardBorder} bg-paper transition-all hover:shadow-[0_8px_22px_rgba(0,0,0,0.08)] ${post.hidden ? "bg-red-50/40" : ""}`}>
      <div className={`w-1 shrink-0 ${spineColor}`} />
      <div className="flex-1 p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className={`inline-flex items-center rounded px-2.5 py-1 text-[9.5px] font-mont font-extrabold uppercase tracking-[0.14em] ${status === "open" ? "bg-brand text-ink" : "bg-surface2 text-ink-soft"}`} data-testid={`collaboration-badge-${post.id}${testIdSuffix}`}>
              {post.collab_kind === "njm" ? "NJM Collaboration" : "Member Collaboration"}
            </span>
            {status === "open" && (
              <span className="font-mont text-[11px] font-semibold text-faint tracking-wide">
                {complete ? "Team complete" : "Team forming"} · {timeAgo(post.created_at)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {status === "cancelled" ? (
              <span className="font-mont text-[11px] font-bold text-red-500">Cancelled · {timeAgo(post.created_at)}</span>
            ) : status === "closed" ? (
              <span className="font-mont text-[11px] font-bold text-ink-soft">Closed · {timeAgo(post.created_at)}</span>
            ) : complete ? (
              <span className="font-mont text-[11px] font-bold text-emerald-700">Team complete ✓</span>
            ) : (
              <>
                <div className="flex items-center ml-[7px]">
                  {stackAvatars.map((a, i) => <AvatarChip key={`${a.key}-${i}`} label={a.label} photo={a.photo} freelancerId={a.freelancerId} tone={a.tone} first={i === 0} />)}
                  {Array.from({ length: dashedCount }).map((_, i) => (
                    <div key={`open-${i}`} className="w-[26px] h-[26px] -ml-[7px] rounded-full border-[1.5px] border-dashed border-black/25 bg-paper shrink-0" />
                  ))}
                </div>
                <span className="font-mont text-[11px] font-bold text-ink" data-testid={`collaboration-open-count-${post.id}${testIdSuffix}`}>{openCount} spot{openCount === 1 ? "" : "s"} open</span>
              </>
            )}
          </div>
        </div>

        <button onClick={() => setShowDetail(true)} className="mt-3.5 block text-left font-mont text-xl sm:text-2xl font-bold leading-tight text-ink hover:underline" data-testid={`collaboration-project-name-${post.id}${testIdSuffix}`}>
          {post.collab_project_name}
        </button>
        <p className="mt-2 font-mont text-[13.5px] leading-relaxed text-ink-soft line-clamp-2">{post.text}</p>
        <div className="mt-2.5 font-mont text-[11.5px] font-semibold tracking-wide text-faint">
          {[post.collab_city, post.collab_location_type, post.collab_project_type].filter(Boolean).join(" · ")}
        </div>

        {slots.length > 0 && status === "open" && !complete && showRoleChips && (
          <>
            <div className="mt-5 font-mont text-[9.5px] font-extrabold uppercase tracking-[0.15em] text-faint">People we're looking for</div>
            <div className="mt-2.5 flex flex-wrap gap-2.5">
              {slots.map((s) => {
                const roleOpen = Math.max(0, s.quantity_required - s.quantity_filled);
                return roleOpen === 0 ? (
                  <div key={s.slot_id} className="rounded-lg border border-hairline bg-surface2/60 px-3 py-2">
                    <div className="font-mont text-xs font-bold text-faint line-through">{s.role}</div>
                  </div>
                ) : (
                  <div key={s.slot_id} className="rounded-lg border border-black/10 bg-surface2/40 px-3 py-2">
                    <div className="font-mont text-xs font-bold text-ink">{s.role} <span className="text-faint">× {roleOpen}</span></div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="mt-5 border-t border-black/10" />
        <div className="mt-3.5 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-6 w-6 rounded-full bg-brand/25 text-ink flex items-center justify-center font-mont text-[10px] font-bold shrink-0">{(post.author_name || "?")[0]}</div>
            <span className="font-mont text-[11.5px] text-ink-soft truncate">
              Started by <MemberLink freelancerId={post.author_freelancer_id} className="font-bold text-ink hover:underline" testId={`collaboration-author-link-${post.id}${testIdSuffix}`}>{post.author_name}</MemberLink> · {post.collab_creator_role}
            </span>
          </div>
          <div className="flex items-center gap-3.5 shrink-0">
            <button onClick={report} disabled={reported} data-testid={`community-report-btn-${post.id}${testIdSuffix}`} className="inline-flex items-center gap-1 font-mont text-[11px] text-faint hover:text-ink disabled:opacity-40">
              <Flag className="h-3 w-3" /> {reported ? "Reported" : "Report"}
            </button>
            {isModerator && (
              <button onClick={() => moderate("hide", { hidden: !post.hidden })} disabled={busy} data-testid={`community-hide-btn-${post.id}${testIdSuffix}`} className="inline-flex items-center gap-1 font-mont text-[11px] text-faint hover:text-ink">
                {post.hidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />} {post.hidden ? "Unhide" : "Hide"}
              </button>
            )}
            {canManage && (
              <button onClick={() => { setAutoOpenApplications(true); setShowDetail(true); }} data-testid={`collaboration-manage-applications-btn-${post.id}${testIdSuffix}`}
                className="inline-flex items-center gap-1 font-mont text-[11px] font-semibold text-ink hover:underline">
                Manage applications
              </button>
            )}
            {canDelete && (
              <ConfirmDialog testIdPrefix={`community-delete-post-${post.id}${testIdSuffix}`} title="Delete this collaboration?"
                description="This removes the post from The Floor and cannot be undone."
                confirmLabel="Delete" onConfirm={() => moderate("delete")}
                trigger={<button disabled={busy} data-testid={`community-delete-btn-${post.id}${testIdSuffix}`} className="inline-flex items-center gap-1 font-mont text-[11px] text-red-400 hover:text-red-500"><Trash2 className="h-3 w-3" /> Delete</button>}
              />
            )}
            <button onClick={() => setShowDetail(true)} data-testid={`collaboration-view-btn-${post.id}${testIdSuffix}`}
              className={`rounded-lg px-4 py-2 font-mont text-[11.5px] font-bold transition-colors ${complete ? "border border-black/20 text-ink hover:bg-surface2" : "bg-ink text-paper hover:bg-ink/85"}`}>
              {complete ? "See the team →" : `View ${post.collab_kind === "njm" ? "NJM Collaboration" : "Member Collaboration"} →`}
            </button>
          </div>
        </div>
      </div>

      {showDetail && (
        <CollaborationDetailModal
          opportunityId={post.opportunity_id} viewerIsFreelancer={viewerIsFreelancer}
          onClose={() => { setShowDetail(false); setAutoOpenApplications(false); }}
          onChanged={() => { onChanged?.(); }}
          autoOpenApplications={autoOpenApplications}
        />
      )}
    </div>
  );
}
